import { useMemo, useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { motion } from "framer-motion";
import { fetchCalendar, fetchStats } from "../lib/api";
import PageHeader from "../components/PageHeader";
import SeasonTimeline from "../components/SeasonTimeline";
import StatsCharts from "../components/StatsCharts";
import VarietySearch from "../components/VarietySearch";
import { Reveal } from "../components/Reveal";

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-x-8 gap-y-3 font-sans text-sm text-forest-light">
      <span className="flex items-center gap-2"><span className="h-3 w-3 bg-harvest" />Indian harvest & market window</span>
      <span className="flex items-center gap-2"><span className="h-3 w-3 bg-terracotta" />Imported market window</span>
      <span className="flex items-center gap-2"><span className="h-3 w-3 bg-unconfirmed/70" />Out of season</span>
      <span className="flex items-center gap-2"><span className="flex h-3 w-3 items-center justify-center bg-harvest"><span className="h-1 w-1 rounded-full bg-cream-100 ring-1 ring-gold" /></span>Peak month</span>
    </div>
  );
}

export default function Calendar() {
  const { data: families } = useSWR("calendar", fetchCalendar);
  const { data: stats } = useSWR("stats", fetchStats);
  const [params, setParams] = useSearchParams();
  const [active, setActive] = useState(params.get("fruit") || "mango");
  const [highlight, setHighlight] = useState(null);
  const detailRef = useRef(null);

  useEffect(() => {
    const q = params.get("fruit");
    if (q) setActive(q);
  }, [params]);

  const selected = useMemo(
    () => (families || []).find((f) => f.slug === active) || (families || [])[0],
    [families, active]
  );

  const select = (slug) => {
    setActive(slug);
    setHighlight(null);
    setParams({ fruit: slug }, { replace: true });
  };

  const pickVariety = (slug, key) => {
    setActive(slug);
    setHighlight(key);
    setParams({ fruit: slug }, { replace: true });
    setTimeout(() => {
      detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
  };

  return (
    <>
      <PageHeader
        overline="The sourcing calendar"
        lines={["The harvest manifest."]}
        intro="Not just “mangoes in season” — every named variety, its growing region, and the months it's actually available to buy. Indian harvests and import partners, timed against each other."
      />

      <section className="border-b border-border bg-cream-100">
        <div className="mx-auto flex max-w-[1400px] flex-col gap-5 px-5 py-6 md:flex-row md:items-center md:justify-between md:px-10">
          <Legend />
          <VarietySearch families={families} onPick={pickVariety} />
        </div>
      </section>

      <section data-testid="calendar-explorer" className="mx-auto max-w-[1400px] px-5 py-14 md:px-10 md:py-20">
        <div className="grid gap-8 lg:grid-cols-[300px_1fr]">
          {/* Sidebar */}
          <aside className="lg:sticky lg:top-28 lg:h-fit">
            <div className="hide-scrollbar flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:overflow-visible lg:pb-0">
              {(families || []).map((f) => {
                const isActive = selected?.slug === f.slug;
                return (
                  <button
                    key={f.slug}
                    data-testid={`calendar-select-${f.slug}`}
                    onClick={() => select(f.slug)}
                    className={`group flex flex-none items-center gap-3 rounded-sm border px-4 py-3 text-left transition-colors duration-300 lg:w-full ${
                      isActive
                        ? "border-forest bg-cream-200"
                        : "border-border bg-cream-100 hover:border-forest/40"
                    }`}
                  >
                    <img src={f.image} alt="" className="h-9 w-9 flex-none object-contain" loading="lazy" />
                    <span className="min-w-0">
                      <span className={`block font-display text-xl leading-tight ${isActive ? "text-forest" : "text-forest-light"}`}>
                        {f.name}
                      </span>
                      <span className="block font-mono text-[0.62rem] uppercase tracking-wide text-forest-muted">
                        {f.variety_count} var · {f.months_supply_count}/12 mo
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* Detail panel */}
          <div className="min-w-0" ref={detailRef}>
            {selected && (
              <motion.div
                key={selected.slug}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="mb-8 flex items-center gap-5 border-b border-border pb-8">
                  <img src={selected.image} alt={selected.name} className="h-16 w-16 flex-none object-contain" />
                  <div>
                    <h2 className="font-display text-4xl font-semibold text-forest md:text-5xl">{selected.name}</h2>
                    <p className="mt-1 font-mono text-xs uppercase tracking-wider text-forest-muted">
                      {selected.variety_count} varieties tracked · in supply {selected.months_supply_count} of 12 months
                    </p>
                  </div>
                </div>
                <div className="hide-scrollbar overflow-x-auto">
                  <SeasonTimeline family={selected} highlight={highlight} />
                </div>
                <p className="mt-8 max-w-2xl text-sm leading-relaxed text-forest-light">{selected.description}</p>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Summary statistics */}
      <section className="border-t border-border bg-cream-200">
        <div className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28">
          <Reveal className="mb-12 max-w-2xl">
            <h2 className="font-display text-3xl font-medium leading-tight text-forest md:text-5xl">
              The catalogue, read as data
            </h2>
          </Reveal>
          <StatsCharts stats={stats} />
        </div>
      </section>
    </>
  );
}
